import { z } from "zod";

import { pageRefSchema, reviewStateSchema } from "../schema";

const sceneIdSchema = z.enum(["M5S1", "M5S2", "M5S3"]);
const taskIdSchema = z.enum(["T15", "T16", "T17"]);
const nodeIdSchema = z.string().regex(/^M5S[1-3]_N0[1-9]$/);
const claimIdSchema = z.string().regex(/^M5S[1-3]_CLAIM_[A-Z0-9_]+$/);

const textbookSourceSchema = z.object({
  sourceCategory: z.literal("教材正文"),
  provenanceType: z.literal("textbook"),
  sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
  citationLabel: z.literal("《中国共产党简史》第三章"),
  pageRefs: z.array(pageRefSchema).min(1),
});

const assetSuggestionSchema = z.object({
  key: z.string().regex(/^M5S[1-3]_ASSET_[A-Z0-9_]+$/),
  kind: z.enum(["programmatic_3d", "programmatic_2_5d", "archive_media", "interface"]),
  title: z.string().min(1),
  usage: z.string().min(1),
  provenanceGuard: z.string().min(1),
  suggestedVisibleLabel: z.string().min(1).optional(),
});

const presentationTypeSchema = z.enum([
  "archival_growth_timeline",
  "document_evidence_wall",
  "relationship_map",
  "reading_index",
  "causal_summary",
  "theory_publication_timeline",
  "revolution_stage_map",
  "program_matrix",
  "theory_outcome_chain",
  "rectification_chronology",
  "rectification_content_matrix",
  "method_process",
  "forum_quote_evidence",
  "literary_work_archive",
  "resolution_timeline",
  "outcome_evidence_chain",
]);

const interactionSuggestionSchema = z.object({
  kind: z.enum([
    "timeline_compare",
    "evidence_highlight",
    "relationship_build",
    "source_match",
    "causal_sequence",
    "document_sequence",
    "boundary_classify",
    "program_classify",
    "stage_connect",
    "chronology_order",
    "concept_trace",
    "method_sequence",
    "quote_inference",
    "work_evidence_compare",
    "resolution_evidence_sequence",
    "outcome_map",
  ]),
  action: z.string().min(1),
  feedback: z.string().min(1),
  historicalGuard: z.string().min(1),
  formalAssessment: z.literal(false),
});

export const m5BlueprintNodeSchema = z.object({
  id: nodeIdSchema,
  sceneId: sceneIdSchema,
  order: z.number().int().positive(),
  nodeType: z.enum(["lecture", "document_study", "solemn_archive_review"]),
  title: z.string().min(1),
  timeAnchor: z.string().min(1),
  estimatedSeconds: z.number().int().positive(),
  claimId: claimIdSchema,
  claim: z.string().min(1),
  bodyZh: z.string().min(1),
  presentationType: presentationTypeSchema,
  tone: z.enum(["academic_documentary", "solemn_evidence_based"]),
  assetSuggestions: z.array(assetSuggestionSchema).min(1),
  interactionSuggestion: interactionSuggestionSchema,
  source: textbookSourceSchema,
  reviewState: reviewStateSchema,
});

const narrationTimingSchema = z.object({
  mainNarrationSeconds: z.number().int().positive(),
  totalNarrationSeconds: z.number().int().positive(),
});

const expectedSequences = {
  M5S1: ["M5S1_N01", "M5S1_N02", "M5S1_N03", "M5S1_N04", "M5S1_N05", "T15"],
  M5S2: ["M5S2_N01", "M5S2_N02", "M5S2_N03", "M5S2_N04"],
  M5S3: [
    "M5S3_N01",
    "M5S3_N02",
    "M5S3_N03",
    "M5S3_N04",
    "M5S3_N05",
    "M5S3_N06",
    "M5S3_N07",
    "T16",
    "T17",
  ],
} as const;

const expectedTaskMap = {
  M5S1: ["T15"],
  M5S2: [],
  M5S3: ["T16", "T17"],
} as const;

const expectedMainNarrationSeconds = {
  M5S1: 240,
  M5S2: 180,
  M5S3: 240,
} as const;

export const m5SceneBlueprintSchema = z
  .object({
    id: sceneIdSchema,
    order: z.number().int().min(1).max(3),
    title: z.string().min(1),
    identity: z.literal("抗战史料研学员"),
    timeLabel: z.string().min(1),
    mainVenue: z.string().min(1),
    archiveSlices: z.array(z.string().min(1)),
    taskIds: z.array(taskIdSchema).max(2),
    sequence: z.array(z.string().min(1)).min(1),
    nodes: z.array(m5BlueprintNodeSchema).min(1),
    timing: narrationTimingSchema,
    reviewState: reviewStateSchema,
  })
  .superRefine((scene, context) => {
    const expectedSequence = expectedSequences[scene.id];
    if (scene.sequence.join("|") !== expectedSequence.join("|")) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点与正式任务顺序不符合冻结基线。`,
        path: ["sequence"],
      });
    }
    if (scene.taskIds.join("|") !== expectedTaskMap[scene.id].join("|")) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}正式任务映射不符合冻结基线。`,
        path: ["taskIds"],
      });
    }
    if (scene.nodes.some((node) => node.sceneId !== scene.id)) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}存在归属错误的教材节点。`,
        path: ["nodes"],
      });
    }
    const nodeSeconds = scene.nodes.reduce((total, node) => total + node.estimatedSeconds, 0);
    if (
      scene.timing.mainNarrationSeconds !== nodeSeconds ||
      scene.timing.totalNarrationSeconds !== nodeSeconds ||
      nodeSeconds !== expectedMainNarrationSeconds[scene.id]
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}须保持原方案约${expectedMainNarrationSeconds[scene.id] / 60}分钟主体讲授，并与节点时长闭环。`,
        path: ["timing"],
      });
    }
    const orders = scene.nodes.map((node) => node.order);
    if (
      new Set(orders).size !== orders.length ||
      orders.some((order, index) => index > 0 && order <= (orders[index - 1] ?? 0))
    ) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}节点order必须唯一且严格递增。`,
        path: ["nodes"],
      });
    }
    if (scene.id === "M5S2" && scene.sequence.some((item) => /^T\d+$/.test(item))) {
      context.addIssue({
        code: "custom",
        message: "M5S2不设正式任务。",
        path: ["sequence"],
      });
    }
    if (scene.id === "M5S3" && scene.nodes.some((node) => node.tone !== "solemn_evidence_based")) {
      context.addIssue({
        code: "custom",
        message: "M5S3整风、文艺座谈会与《决议》节点必须保持庄重史料语调。",
        path: ["nodes"],
      });
    }
  });

const reviewNoteSchema = z.object({
  id: z.string().regex(/^M5_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  priority: z.enum(["before_frontend_release", "before_formal_release"]),
  state: z.literal("open"),
  note: z.string().min(1),
});

export const m5BlueprintSchema = z
  .object({
    id: z.literal("M5"),
    version: z.literal("1.0.0"),
    title: z.literal("加强党的建设、推进马克思主义中国化和开展整风运动"),
    identity: z.literal("抗战史料研学员"),
    pageRange: z.object({ print: z.literal("97—103"), pdf: z.literal("117—123") }),
    sceneIds: z.tuple([z.literal("M5S1"), z.literal("M5S2"), z.literal("M5S3")]),
    taskIds: z.tuple([z.literal("T15"), z.literal("T16"), z.literal("T17")]),
    localExtensionCount: z.literal(0),
    scenes: z.array(m5SceneBlueprintSchema).length(3),
    timing: narrationTimingSchema,
    reviewState: reviewStateSchema,
    reviewNotes: z.array(reviewNoteSchema).min(1),
  })
  .superRefine((blueprint, context) => {
    if (blueprint.scenes.map((scene) => scene.id).join("|") !== blueprint.sceneIds.join("|")) {
      context.addIssue({
        code: "custom",
        message: "M5三个场景必须按M5S1、M5S2、M5S3声明。",
        path: ["scenes"],
      });
    }
    const allNodes = blueprint.scenes.flatMap((scene) => scene.nodes);
    const nodeIds = allNodes.map((node) => node.id);
    const claimIds = allNodes.map((node) => node.claimId);
    if (new Set(nodeIds).size !== nodeIds.length) {
      context.addIssue({ code: "custom", message: "M5节点ID必须唯一。", path: ["scenes"] });
    }
    if (new Set(claimIds).size !== claimIds.length) {
      context.addIssue({ code: "custom", message: "M5 claimId必须唯一。", path: ["scenes"] });
    }
    const moduleSeconds = blueprint.scenes.reduce(
      (total, scene) => total + scene.timing.totalNarrationSeconds,
      0,
    );
    if (
      moduleSeconds !== 660 ||
      blueprint.timing.mainNarrationSeconds !== moduleSeconds ||
      blueprint.timing.totalNarrationSeconds !== moduleSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: "M5模块讲授时长必须由4/3/4分钟三个场景汇总为660秒。",
        path: ["timing"],
      });
    }
  });

export type M5BlueprintNode = z.infer<typeof m5BlueprintNodeSchema>;
export type M5SceneBlueprint = z.infer<typeof m5SceneBlueprintSchema>;
export type M5Blueprint = z.infer<typeof m5BlueprintSchema>;

const textbookSource = (print: string, pdf: string): M5BlueprintNode["source"] => ({
  sourceCategory: "教材正文",
  provenanceType: "textbook",
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  citationLabel: "《中国共产党简史》第三章",
  pageRefs: [{ print, pdf }],
});

export const m5Blueprint = {
  id: "M5",
  version: "1.0.0",
  title: "加强党的建设、推进马克思主义中国化和开展整风运动",
  identity: "抗战史料研学员",
  pageRange: { print: "97—103", pdf: "117—123" },
  sceneIds: ["M5S1", "M5S2", "M5S3"],
  taskIds: ["T15", "T16", "T17"],
  localExtensionCount: 0,
  scenes: [
    {
      id: "M5S1",
      order: 1,
      title: "党的建设“伟大的工程”",
      identity: "抗战史料研学员",
      timeLabel: "1938年底—1939年10月",
      mainVenue: "延安中共中央驻地",
      archiveSlices: ["全国党员发展与党建文献档案"],
      taskIds: ["T15"],
      sequence: ["M5S1_N01", "M5S1_N02", "M5S1_N03", "M5S1_N04", "M5S1_N05", "T15"],
      nodes: [
        {
          id: "M5S1_N01",
          sceneId: "M5S1",
          order: 10,
          nodeType: "lecture",
          title: "党员队伍迅速发展提出自身建设新要求",
          timeAnchor: "全民族抗战爆发—1938年底",
          estimatedSeconds: 45,
          claimId: "M5S1_CLAIM_MEMBERSHIP_GROWTH",
          claim: "到1938年底，全国党员人数由全民族抗战爆发时的4万多人增加到50多万人。",
          bodyZh:
            "在全民族抗日战争的推动下，中国共产党迅速发展与壮大。到1938年底，全国党员人数从全民族抗战爆发时的4万多人增加到50多万人。这对党的自身建设提出新的要求。",
          presentationType: "archival_growth_timeline",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S1_ASSET_MEMBERSHIP_TIMELINE",
              kind: "programmatic_2_5d",
              title: "4万多人至50多万人党员发展时间轴",
              usage: "用时间、人数与建设要求三层信息解释规模变化带来的党建命题。",
              provenanceGuard:
                "人数仅按教材口径呈现；程序化图形须标明教学示意，不伪装为历史统计原表。",
              suggestedVisibleLabel: "数据关系示意·依据教材",
            },
          ],
          interactionSuggestion: {
            kind: "timeline_compare",
            action:
              "拖动时间轴对照全民族抗战爆发时与1938年底两个节点，并归纳党员规模变化带来的建设要求。",
            feedback: "系统分别反馈人数变化事实与“对党的自身建设提出新要求”的因果归纳。",
            historicalGuard: "不据两个教材数字推算未经来源支持的精确增幅、地域分布或组织构成。",
            formalAssessment: false,
          },
          source: textbookSource("97—98", "117—118"),
          reviewState: "pending_review",
        },
        {
          id: "M5S1_N02",
          sceneId: "M5S1",
          order: 20,
          nodeType: "document_study",
          title: "《〈共产党人〉发刊词》与“伟大的工程”",
          timeAnchor: "1939年8月—10月",
          estimatedSeconds: 60,
          claimId: "M5S1_CLAIM_GREAT_PROJECT",
          claim:
            "毛泽东在《〈共产党人〉发刊词》中提出党的建设总目标、总任务，并把党的建设称为“伟大的工程”。",
          bodyZh:
            "1939年8月，中央政治局作出《关于巩固党的决定》。10月，毛泽东发表《〈共产党人〉发刊词》，提出党的建设的总目标、总任务，即“建设一个全国范围的、广大群众性的、思想上政治上组织上完全巩固的布尔什维克化的中国共产党”，把党的建设称为“伟大的工程”，指出党的建设要紧密围绕党的政治路线进行。",
          presentationType: "document_evidence_wall",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S1_ASSET_GREAT_PROJECT_DOCUMENT",
              kind: "archive_media",
              title: "党建总目标与“伟大的工程”文献卡",
              usage: "分层标注文件、总目标总任务、工程比喻及政治路线关系。",
              provenanceGuard:
                "正式文献图像须核验来源与权利；没有原件时使用教材摘录卡，不制作仿真手稿。",
            },
          ],
          interactionSuggestion: {
            kind: "evidence_highlight",
            action: "在教材摘录卡中依次标出组织范围、群众基础、巩固维度与“伟大的工程”表述。",
            feedback: "系统依据四类证据反馈遗漏项，并归纳党的建设总目标、总任务的完整结构。",
            historicalGuard: "仅标注教材载录文字，不把界面排版误称为原始文献版式。",
            formalAssessment: false,
          },
          source: textbookSource("98", "118"),
          reviewState: "pending_review",
        },
        {
          id: "M5S1_N03",
          sceneId: "M5S1",
          order: 30,
          nodeType: "lecture",
          title: "三个基本问题、三个主要法宝",
          timeAnchor: "1939年10月",
          estimatedSeconds: 48,
          claimId: "M5S1_CLAIM_THREE_MAGIC_WEAPONS",
          claim: "统一战线、武装斗争、党的建设，是中国共产党在中国革命中战胜敌人的三个主要法宝。",
          bodyZh:
            "毛泽东指出，统一战线问题、武装斗争问题、党的建设问题，是党在中国革命中的三个基本问题。正确地理解这三个问题及其相互关系，就等于正确地领导了全部中国革命。毛泽东总结中国共产党成立以来的历史经验，指出统一战线、武装斗争、党的建设，是中国共产党在中国革命中战胜敌人的三个主要法宝。",
          presentationType: "relationship_map",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S1_ASSET_THREE_MAGIC_WEAPONS",
              kind: "interface",
              title: "统一战线-武装斗争-党的建设关系图",
              usage: "让学习者建立三个基本问题之间的关系，并与正式任务T15衔接。",
              provenanceGuard: "关系连线用于教学归纳，不添加教材未表述的层级或先后顺序。",
            },
          ],
          interactionSuggestion: {
            kind: "relationship_build",
            action: "把统一战线、武装斗争、党的建设三张证据卡放入关系图并说明三者须相互联系理解。",
            feedback: "系统即时指出漏项或将“三大法宝”与“三大作风”混淆的情况。",
            historicalGuard: "不将三者简化为可互相替代的概念，也不引入教材外的第四项。",
            formalAssessment: false,
          },
          source: textbookSource("98—99", "118—119"),
          reviewState: "pending_review",
        },
        {
          id: "M5S1_N04",
          sceneId: "M5S1",
          order: 40,
          nodeType: "document_study",
          title: "党员教育的重要教材",
          timeAnchor: "抗日战争时期党的巩固建设",
          estimatedSeconds: 40,
          claimId: "M5S1_CLAIM_MEMBER_EDUCATION_TEXTS",
          claim: "陈云、刘少奇、张闻天等人的论著为党员教育提供了重要教材。",
          bodyZh:
            "巩固党的中心一环，是加强对党员的培训，提高党员素质。陈云撰写《怎样做一个共产党员》，刘少奇作《论共产党员的修养》的演说，张闻天连续发表《共产党员的权利与义务》等6篇文章。这些论著为党员教育提供了重要教材，在党的建设中发挥了重要作用。",
          presentationType: "reading_index",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S1_ASSET_MEMBER_EDUCATION_INDEX",
              kind: "interface",
              title: "党员教育论著索引",
              usage: "以作者、篇名与党员教育作用建立可检索文献索引。",
              provenanceGuard: "篇名和数量按教材呈现；未核验的版本封面不得作为原件展示。",
            },
          ],
          interactionSuggestion: {
            kind: "source_match",
            action: "将作者、教材列举的论著与“党员教育”作用进行证据匹配。",
            feedback: "系统反馈作者-篇名是否匹配，并提示这些论著共同服务于党员培训和素质提高。",
            historicalGuard: "不推断教材未说明的写作地点、具体授课场次或传播数量。",
            formalAssessment: false,
          },
          source: textbookSource("98", "118"),
          reviewState: "pending_review",
        },
        {
          id: "M5S1_N05",
          sceneId: "M5S1",
          order: 50,
          nodeType: "lecture",
          title: "党的建设伟大工程是政治保证",
          timeAnchor: "全民族抗日战争时期",
          estimatedSeconds: 47,
          claimId: "M5S1_CLAIM_POLITICAL_GUARANTEE",
          claim: "党的建设伟大工程为党在抗日战争中发挥中流砥柱作用提供了强有力的政治保证。",
          bodyZh:
            "把党的建设作为一项伟大工程来推进，是党的一个创举，表明党对加强自身建设重要性的认识更加自觉和深刻。党的建设伟大工程的实施，为党在抗日战争中发挥中流砥柱作用提供了强有力的政治保证。",
          presentationType: "causal_summary",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S1_ASSET_POLITICAL_GUARANTEE_CHAIN",
              kind: "programmatic_2_5d",
              title: "自身建设-政治保证-中流砥柱因果链",
              usage: "把本场景各证据归纳为可回溯的因果链。",
              provenanceGuard: "因果链只归纳教材明确表述，不量化未经验证的组织建设效果。",
            },
          ],
          interactionSuggestion: {
            kind: "causal_sequence",
            action: "按教材逻辑排列“推进伟大工程”“形成政治保证”“发挥中流砥柱作用”三层证据。",
            feedback: "系统反馈因果方向，并回指每一层对应的教材段落。",
            historicalGuard: "不把历史评价转化为娱乐化成长值或虚构绩效指标。",
            formalAssessment: false,
          },
          source: textbookSource("98", "118"),
          reviewState: "pending_review",
        },
      ],
      timing: { mainNarrationSeconds: 240, totalNarrationSeconds: 240 },
      reviewState: "pending_review",
    },
    {
      id: "M5S2",
      order: 2,
      title: "系统阐明新民主主义理论",
      identity: "抗战史料研学员",
      timeLabel: "1939年末—1940年初",
      mainVenue: "延安理论文献研读场景（不虚构具体写作房间）",
      archiveSlices: [],
      taskIds: [],
      sequence: ["M5S2_N01", "M5S2_N02", "M5S2_N03", "M5S2_N04"],
      nodes: [
        {
          id: "M5S2_N01",
          sceneId: "M5S2",
          order: 10,
          nodeType: "document_study",
          title: "回答“中国向何处去”的理论著作",
          timeAnchor: "1939年末—1940年初",
          estimatedSeconds: 35,
          claimId: "M5S2_CLAIM_THEORY_WORKS",
          claim:
            "毛泽东发表《中国革命和中国共产党》《新民主主义论》等著作，系统阐明新民主主义理论。",
          bodyZh:
            "全民族抗日战争爆发以来，“中国向何处去”的问题尖锐地摆在每一个中国人面前。1939年、1940年之交，为了将丰富的中国革命经验系统化，阐明党的理论和纲领，回答中国向何处去的问题，继发表《〈共产党人〉发刊词》之后，毛泽东又接连发表《中国革命和中国共产党》《新民主主义论》等重要理论著作。",
          presentationType: "theory_publication_timeline",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S2_ASSET_THEORY_PUBLICATION_TIMELINE",
              kind: "interface",
              title: "1939年至1940年理论著作时间轴",
              usage: "以文献时间轴连接时代问题、著作与理论回答，不构建虚构写作现场。",
              provenanceGuard:
                "只呈现教材确认的著作与时间范围；不得虚构具体写作房间、室内陈设或创作过程。",
            },
          ],
          interactionSuggestion: {
            kind: "document_sequence",
            action: "按教材叙述顺序排列三份理论文献，并把它们连接到“中国向何处去”的时代问题。",
            feedback: "系统反馈文献顺序与其共同承担的理论阐明功能。",
            historicalGuard: "交互发生在文献研读界面，不让学习者扮演作者或进入虚构窑洞写作房间。",
            formalAssessment: false,
          },
          source: textbookSource("98—99", "118—119"),
          reviewState: "pending_review",
        },
        {
          id: "M5S2_N02",
          sceneId: "M5S2",
          order: 20,
          nodeType: "lecture",
          title: "中国革命的两个阶段与新民主主义革命性质",
          timeAnchor: "1919年五四运动以后至抗战时期的理论总结",
          estimatedSeconds: 42,
          claimId: "M5S2_CLAIM_TWO_REVOLUTION_STAGES",
          claim:
            "中国共产党领导的中国革命包括民主主义革命和社会主义革命两个阶段，五四运动以后的中国民主革命已是新民主主义革命。",
          bodyZh:
            "毛泽东揭示中国半殖民地半封建社会的性质和主要特征、近代中国社会的主要矛盾和中国革命发生发展的原因，指出中国共产党领导的整个中国革命运动，是包括民主主义革命和社会主义革命两个阶段在内的全部革命运动。1919年五四运动以后的中国民主革命，已经是无产阶级领导的人民大众的反帝反封建的新民主主义革命。",
          presentationType: "revolution_stage_map",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S2_ASSET_REVOLUTION_STAGE_MAP",
              kind: "programmatic_2_5d",
              title: "民主主义革命与社会主义革命阶段图",
              usage: "用边界清晰的阶段图解释革命性质、领导力量和衔接关系。",
              provenanceGuard: "阶段图是理论关系示意，不将两个阶段混同或压缩为同一历史事件。",
              suggestedVisibleLabel: "理论关系示意·依据教材",
            },
          ],
          interactionSuggestion: {
            kind: "boundary_classify",
            action: "依据教材概念把革命性质、领导力量和目标放入相应阶段，观察错误混同产生的反馈。",
            feedback: "系统解释新民主主义革命的性质及两个阶段的严格区别。",
            historicalGuard: "不把抽象理论分类伪装为学习者能够改写历史进程的选择。",
            formalAssessment: false,
          },
          source: textbookSource("99", "119"),
          reviewState: "pending_review",
        },
        {
          id: "M5S2_N03",
          sceneId: "M5S2",
          order: 30,
          nodeType: "document_study",
          title: "新民主主义革命阶段的政治、经济、文化纲领",
          timeAnchor: "新民主主义革命阶段",
          estimatedSeconds: 55,
          claimId: "M5S2_CLAIM_BASIC_PROGRAM",
          claim: "新民主主义革命阶段的基本纲领包括政治、经济、文化三个方面。",
          bodyZh:
            "毛泽东阐明中国共产党在新民主主义革命阶段的基本纲领。政治上，要建立无产阶级领导下的一切反帝反封建的人们联合专政的民主共和国。经济上，要没收操纵国计民生的大银行、大工业、大商业，建立国营经济；没收地主土地归农民所有，并引导农民发展合作经济；允许民族资本主义经济的发展和富农经济的存在。文化上，是废除封建买办文化，发展民族的科学的大众的文化。",
          presentationType: "program_matrix",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S2_ASSET_BASIC_PROGRAM_MATRIX",
              kind: "interface",
              title: "政治-经济-文化基本纲领矩阵",
              usage: "用三个并列证据区承载纲领要点，支持学习者分类与回查。",
              provenanceGuard: "矩阵文字按教材呈现，不擅自扩写为后续历史时期的政策。",
            },
          ],
          interactionSuggestion: {
            kind: "program_classify",
            action: "将教材摘录的纲领要点归入政治、经济、文化三栏，并打开来源页回查。",
            feedback: "系统对每一项给出分类理由，强调三方面共同构成基本纲领。",
            historicalGuard: "不把理论纲领设计成资源经营或国家治理游戏。",
            formalAssessment: false,
          },
          source: textbookSource("99", "119"),
          reviewState: "pending_review",
        },
        {
          id: "M5S2_N04",
          sceneId: "M5S2",
          order: 40,
          nodeType: "lecture",
          title: "革命前途、理论成熟与历史作用",
          timeAnchor: "新民主主义理论系统阐明之后",
          estimatedSeconds: 48,
          claimId: "M5S2_CLAIM_THEORY_SIGNIFICANCE",
          claim: "新民主主义理论的提出和系统阐明，标志着毛泽东思想得到多方面展开而趋于成熟。",
          bodyZh:
            "毛泽东指出，新民主主义革命的发展前途必然是社会主义。新民主主义革命和社会主义革命是两个不同的革命阶段，不能“毕其功于一役”，但两个革命阶段必须也必然是衔接的，不容横插一个资产阶级专政。新民主主义理论的提出，使全党对中国现阶段革命的性质、内容、领导权和发展前途有了明确而完整的认识。这一理论成为引导中国人民自觉地在复杂环境中不断前进的旗帜，是马克思主义中国化的重大理论成果，标志着毛泽东思想得到多方面展开而趋于成熟。",
          presentationType: "theory_outcome_chain",
          tone: "academic_documentary",
          assetSuggestions: [
            {
              key: "M5S2_ASSET_THEORY_OUTCOME_CHAIN",
              kind: "interface",
              title: "阶段区别-相互衔接-理论意义证据链",
              usage: "以三层证据链收束新民主主义理论的边界、前途与历史作用。",
              provenanceGuard: "评价性表述按教材层次呈现，不借任务反馈夸大或改写理论结论。",
            },
          ],
          interactionSuggestion: {
            kind: "stage_connect",
            action: "先区分两个革命阶段，再用教材证据建立必然衔接，最后回收理论成熟与历史作用。",
            feedback: "系统分别提示“不能混同”和“必须衔接”两条边界，并显示对应教材句。",
            historicalGuard: "不把理论前途呈现为可任意选择的架空历史分支。",
            formalAssessment: false,
          },
          source: textbookSource("99—100", "119—120"),
          reviewState: "pending_review",
        },
      ],
      timing: { mainNarrationSeconds: 180, totalNarrationSeconds: 180 },
      reviewState: "pending_review",
    },
    {
      id: "M5S3",
      order: 3,
      title: "整风运动和《关于若干历史问题的决议》",
      identity: "抗战史料研学员",
      timeLabel: "1941年5月—1945年4月",
      mainVenue: "延安及各抗日根据地",
      archiveSlices: [],
      taskIds: ["T16", "T17"],
      sequence: [
        "M5S3_N01",
        "M5S3_N02",
        "M5S3_N03",
        "M5S3_N04",
        "M5S3_N05",
        "M5S3_N06",
        "M5S3_N07",
        "T16",
        "T17",
      ],
      nodes: [
        {
          id: "M5S3_N01",
          sceneId: "M5S3",
          order: 10,
          nodeType: "solemn_archive_review",
          title: "延安整风运动的展开",
          timeAnchor: "1941年5月—1942年2月",
          estimatedSeconds: 42,
          claimId: "M5S3_CLAIM_RECTIFICATION_LAUNCH",
          claim: "从1941年5月起，党通过报告、会议和讲演逐步展开普遍的马克思主义思想教育运动。",
          bodyZh:
            "遵义会议后，党的路线已经走上马克思主义的正确轨道，但对曾经给党的事业造成严重危害的主观主义、教条主义还没有来得及从思想上进行认真清理。1941年5月，毛泽东在延安高级干部会议上作《改造我们的学习》的报告。9月至10月，中央政治局召开扩大会议，党的高级干部开始学习和研究党的历史，总结党的历史经验，以求从政治路线上分清是非，达到基本一致的认识，为全党普遍整风作准备。1942年2月，毛泽东先后作《整顿党的作风》和《反对党八股》的讲演，整风运动在全党普遍展开。",
          presentationType: "rectification_chronology",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_RECTIFICATION_CHRONOLOGY",
              kind: "archive_media",
              title: "1941年至1942年整风展开文献时间轴",
              usage: "以报告、会议、讲演和全党展开四类档案节点呈现过程。",
              provenanceGuard:
                "没有权威图像时使用教材文献卡；不得虚构会议室、写作房间或参与者对话。",
            },
          ],
          interactionSuggestion: {
            kind: "chronology_order",
            action: "依据日期把报告、扩大会议、两篇讲演与全党普遍展开排列成史料时间轴。",
            feedback: "系统逐节点说明从思想准备到普遍展开的过程，不以速度积分评价。",
            historicalGuard: "不让学习者扮演真实领导人，也不虚构会议决定过程。",
            formalAssessment: false,
          },
          source: textbookSource("100—101", "120—121"),
          reviewState: "pending_review",
        },
        {
          id: "M5S3_N02",
          sceneId: "M5S3",
          order: 20,
          nodeType: "document_study",
          title: "整风内容与最主要任务",
          timeAnchor: "1942年全党普遍整风",
          estimatedSeconds: 34,
          claimId: "M5S3_CLAIM_RECTIFICATION_CONTENT",
          claim:
            "全党普遍整风反对主观主义、宗派主义和党八股，其中反对主观主义以整顿学风是最主要任务。",
          bodyZh:
            "全党普遍整风的内容是反对主观主义、宗派主义、党八股以树立马克思主义的作风。反对主观主义以整顿学风，是整风运动最主要的任务；反对宗派主义以整顿党风、反对党八股以整顿文风，也是整风运动的重要任务。",
          presentationType: "rectification_content_matrix",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_RECTIFICATION_CONTENT_MATRIX",
              kind: "interface",
              title: "整风对象-领域-任务层级证据矩阵",
              usage: "分层呈现三项对应关系，并突出最主要任务的教材依据。",
              provenanceGuard: "矩阵用于史料梳理；不得把整风内容娱乐化为清除、战斗或惩罚动画。",
            },
          ],
          interactionSuggestion: {
            kind: "concept_trace",
            action: "先在教材摘录中追踪三项对象和对应领域，再标出“最主要任务”的证据句。",
            feedback: "系统区分三项重要任务与其中的最主要任务，为T16、T17提供可回查依据。",
            historicalGuard: "本节点不计入正式成绩，不提前替代T16或T17。",
            formalAssessment: false,
          },
          source: textbookSource("101", "121"),
          reviewState: "pending_review",
        },
        {
          id: "M5S3_N03",
          sceneId: "M5S3",
          order: 30,
          nodeType: "document_study",
          title: "整风的方法",
          timeAnchor: "整风运动普遍展开期间",
          estimatedSeconds: 32,
          claimId: "M5S3_CLAIM_RECTIFICATION_METHOD",
          claim: "整风以阅读文件、联系实际进行检查、开展批评与自我批评为方法，并特别强调自我批评。",
          bodyZh:
            "整风的方法，是认真阅读整风文件，联系个人的思想、工作、历史以及自己所在地区部门的工作进行检查，开展批评与自我批评，弄清犯错误的环境、性质和原因，逐步取得思想认识上的一致，提出努力的方向。在开展批评与自我批评中，特别强调自我批评。毛泽东强调，对于人的处理取慎重态度，既不含糊敷衍，又不损害同志。",
          presentationType: "method_process",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_METHOD_PROCESS",
              kind: "interface",
              title: "阅读-联系实际-检查-批评与自我批评方法链",
              usage: "以教材步骤构成可回查的方法流程，呈现慎重态度要求。",
              provenanceGuard: "不采集学习者隐私，不要求公开真实个人经历，不模拟批斗或惩罚场景。",
            },
          ],
          interactionSuggestion: {
            kind: "method_sequence",
            action:
              "把教材中的阅读、联系实际、检查、批评与自我批评排列为方法链，并为每一步关联原文。",
            feedback: "系统说明方法目标是弄清原因、取得认识一致并提出努力方向。",
            historicalGuard: "只分析历史方法，不要求学习者披露个人思想、经历或敏感信息。",
            formalAssessment: false,
          },
          source: textbookSource("101", "121"),
          reviewState: "pending_review",
        },
        {
          id: "M5S3_N04",
          sceneId: "M5S3",
          order: 40,
          nodeType: "solemn_archive_review",
          title: "延安文艺座谈会与文艺为人民大众",
          timeAnchor: "1942年5月",
          estimatedSeconds: 38,
          claimId: "M5S3_CLAIM_LITERATURE_FOR_PEOPLE",
          claim: "延安文艺座谈会强调文艺为什么人的根本问题，指出文学艺术为人民大众、首先为工农兵。",
          bodyZh:
            "在整风运动中，党中央于1942年5月召开延安文艺座谈会。毛泽东在讲话中强调：“为什么人的问题，是一个根本的问题，原则的问题。”“我们的文学艺术都是为人民大众的，首先是为工农兵的。”在延安文艺座谈会讲话精神指引下，广大文艺工作者纷纷奔向抗战前线，深入农村、部队、工厂，接触群众，体验生活，创作反映现实生活的优秀作品。",
          presentationType: "forum_quote_evidence",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_FORUM_QUOTE_EVIDENCE",
              kind: "archive_media",
              title: "延安文艺座谈会教材引文与实践去向证据卡",
              usage: "以教材引文、服务对象和文艺工作者实践去向三层呈现。",
              provenanceGuard: "引文按教材载录；没有权威照片时不得生成并冒充座谈会档案影像。",
            },
          ],
          interactionSuggestion: {
            kind: "quote_inference",
            action: "从两段教材引文中标出服务对象与根本问题，再连接文艺工作者走向群众的实践证据。",
            feedback: "系统反馈引文与实践之间的对应关系，避免将文艺方针简化为作品标签。",
            historicalGuard: "不进行真人角色扮演，不让学习者代写历史人物讲话。",
            formalAssessment: false,
          },
          source: textbookSource("101", "121"),
          reviewState: "pending_review",
        },
        {
          id: "M5S3_N05",
          sceneId: "M5S3",
          order: 50,
          nodeType: "solemn_archive_review",
          title: "《白毛女》等文艺成果",
          timeAnchor: "延安文艺座谈会之后",
          estimatedSeconds: 30,
          claimId: "M5S3_CLAIM_LITERARY_WORKS",
          claim: "《白毛女》等作品反映现实生活，其中《白毛女》是中国第一部新歌剧。",
          bodyZh:
            "在延安文艺座谈会讲话精神指引下，文艺工作者创作了《白毛女》《兄妹开荒》《逼上梁山》《王贵与李香香》等一大批反映现实生活的优秀作品。其中，《白毛女》是由延安鲁迅艺术学院的艺术家们集体创作的中国第一部新歌剧，通过真实而富有传奇色彩的故事，揭示“旧社会把人逼成‘鬼’，新社会把‘鬼’变成人”的鲜明时代主题。",
          presentationType: "literary_work_archive",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_LITERARY_WORK_ARCHIVE",
              kind: "archive_media",
              title: "延安文艺成果档案索引",
              usage: "以作品名、创作主体、文艺类型和教材评价建立庄重的文献索引。",
              provenanceGuard: "封面、剧照和音视频须逐项核验来源与权利；AI复原必须标为复原性创作。",
            },
          ],
          interactionSuggestion: {
            kind: "work_evidence_compare",
            action: "对照作品档案卡，找出《白毛女》的创作主体、文艺类型与时代主题三项教材证据。",
            feedback: "系统逐项回指教材，不以舞台游戏或娱乐化表演替代史料理解。",
            historicalGuard: "不播放未经授权的完整作品，不把苦难主题转化为猎奇画面或闯关奖励。",
            formalAssessment: false,
          },
          source: textbookSource("101—102", "121—122"),
          reviewState: "pending_review",
        },
        {
          id: "M5S3_N06",
          sceneId: "M5S3",
          order: 60,
          nodeType: "solemn_archive_review",
          title: "《关于若干历史问题的决议》",
          timeAnchor: "1943年9月—1945年4月",
          estimatedSeconds: 34,
          claimId: "M5S3_CLAIM_HISTORY_RESOLUTION",
          claim:
            "党的扩大的六届七中全会原则通过《关于若干历史问题的决议》，系统总结党的历史经验教训并统一全党认识。",
          bodyZh:
            "从1943年9月起，中央领导层的整风进行到深入讨论党的历史问题阶段。为了统一高级干部的思想，中央政治局召开扩大会议，讨论党的历史上的路线问题。在深入总结历史经验的基础上，1944年5月至1945年4月，党的扩大的六届七中全会召开。全会原则通过《关于若干历史问题的决议》。《决议》总结建党以来、特别是六届四中全会至遵义会议前这一段党的历史及其基本经验教训，阐述“左”倾错误在政治、军事、组织、思想方面的表现和严重危害，高度评价毛泽东运用马克思主义基本原理解决中国革命问题的杰出贡献，肯定确立毛泽东在全党的领导地位的重大意义，使全党尤其是党的高级干部对中国民主革命基本问题的认识达到在马克思列宁主义基础上的一致。至此，整风运动胜利结束。",
          presentationType: "resolution_timeline",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_HISTORY_RESOLUTION",
              kind: "archive_media",
              title: "六届七中全会与《决议》史料时间轴",
              usage: "以讨论阶段、会议范围、决议内容和认识统一四层史料呈现。",
              provenanceGuard: "不虚构《决议》的写作房间、讨论对话或表决现场；原件图像须核验来源。",
            },
          ],
          interactionSuggestion: {
            kind: "resolution_evidence_sequence",
            action: "按教材顺序连接历史问题讨论、六届七中全会、《决议》内容与认识统一四组证据。",
            feedback: "系统反馈节点先后与历史作用，并区分教材事实、教材评价和界面归纳。",
            historicalGuard: "不让学习者模拟裁决历史人物或改写《决议》结论。",
            formalAssessment: false,
          },
          source: textbookSource("102", "122"),
          reviewState: "pending_review",
        },
        {
          id: "M5S3_N07",
          sceneId: "M5S3",
          order: 70,
          nodeType: "solemn_archive_review",
          title: "整风运动的成效与深远意义",
          timeAnchor: "整风运动胜利结束之后",
          estimatedSeconds: 30,
          claimId: "M5S3_CLAIM_RECTIFICATION_SIGNIFICANCE",
          claim:
            "整风使实事求是的马克思主义思想路线深入人心，实现全党新的团结统一，并奠定重要思想政治基础。",
          bodyZh:
            "整风运动是一次深刻的马克思主义思想教育运动，收到巨大成效。它坚持马克思主义同中国实际相结合的正确方向，使实事求是的马克思主义思想路线在全党范围内深入人心。通过围绕怎样从实际出发对待马克思主义原理、怎样使马克思主义基本原理同中国革命实际相结合以及怎样对待党史重大问题的讨论，巩固了马克思主义思想在党内外的阵地，使干部在思想上大大提高。整风运动实现了在以毛泽东同志为核心的党中央领导下全党新的团结和统一，为抗日战争胜利和新民主主义革命在全国胜利奠定重要的思想政治基础。延安整风运动所积累的经验对党的建设具有重大而深远的意义。",
          presentationType: "outcome_evidence_chain",
          tone: "solemn_evidence_based",
          assetSuggestions: [
            {
              key: "M5S3_ASSET_RECTIFICATION_OUTCOME",
              kind: "interface",
              title: "思想路线-团结统一-思想政治基础证据链",
              usage: "用教材评价性证据收束整风运动的成效与意义。",
              provenanceGuard: "仅呈现教材评价，不制作胜利庆祝动画、积分增长或虚构量化成效。",
            },
          ],
          interactionSuggestion: {
            kind: "outcome_map",
            action: "把教材列出的思想路线、干部提高、团结统一和思想政治基础关联到对应证据句。",
            feedback: "系统解释各项成效之间的逻辑层次，并提示评价性表述的教材出处。",
            historicalGuard: "不把思想教育成效转化为人物等级、政治评分或虚构统计。",
            formalAssessment: false,
          },
          source: textbookSource("102—103", "122—123"),
          reviewState: "pending_review",
        },
      ],
      timing: { mainNarrationSeconds: 240, totalNarrationSeconds: 240 },
      reviewState: "pending_review",
    },
  ],
  timing: { mainNarrationSeconds: 660, totalNarrationSeconds: 660 },
  reviewState: "pending_review",
  reviewNotes: [
    {
      id: "M5_REVIEW_PAGE_REFERENCE",
      scope: "M5全部教材节点",
      priority: "before_frontend_release",
      state: "open",
      note: "原始DeepSeek方案将源PDF第117—123页误写为“书中页码”；本蓝图依逐页渲染核对结果登记书内印刷第97—103页 / 源PDF第117—123页双页码。",
    },
    {
      id: "M5_REVIEW_RESOLUTION_DATE_SCOPE",
      scope: "M5S3 / 《关于若干历史问题的决议》节点",
      priority: "before_formal_release",
      state: "open",
      note: "原始DeepSeek方案以“1945年4月”单点叙述《决议》；教材第102页 / PDF第122页表述为1944年5月至1945年4月党的扩大的六届七中全会召开并原则通过《决议》。本蓝图按教材会议范围呈现，仅登记差异，不改T16、T17或任务归属。",
    },
    {
      id: "M5_REVIEW_TASK_SOURCE_ANCHORS",
      scope: "T15 / T16 / T17",
      priority: "before_frontend_release",
      state: "open",
      note: "现有正式任务的pageRefs仍为空；本蓝图分别以M5S1_N03和M5S3_N02提供教材锚点。本次不修改tasks.ts，后续集成须在不改题干、选项、答案、顺序和归属的前提下补来源字段。",
    },
  ],
} satisfies M5Blueprint;
